import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  permissionGroupBodyRecipe,
  permissionGroupHeaderRecipe,
  permissionRowDescriptionRecipe,
  permissionRowLabelRecipe,
  permissionRowRecipe,
  type PermissionRowRecipeProps,
} from '../recipes/permission.recipe';
import { Badge } from './Badge';
import { Surface } from './Card';
import { Checkbox } from './Checkbox';
import { Label } from './Field';
import { Icon } from './Icon';
import { Inline, Stack } from './Layout';
import { Text } from './Text';

export interface PermissionGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * The select-all checkbox. Rendered in its own click boundary, so ticking
   * it never also folds the group.
   */
  control?: React.ReactNode;
  title: React.ReactNode;
  /** "3/5 permissions" — how much of the group is on. */
  summary?: React.ReactNode;
  /** A badge after the summary: how many rows differ from the role. */
  badge?: React.ReactNode;
  expanded?: boolean;
  /** The header is the toggle; the whole row is the target. */
  onToggle?: () => void;
  /** The PermissionRows. Only rendered while expanded. */
  children?: React.ReactNode;
}

/**
 * One category of the permissions matrix: a header row that folds the rows
 * beneath it.
 *
 *   <PermissionGroup
 *     title="Dispatch"
 *     summary="3/5 permissions"
 *     control={<Checkbox checked={all} onCheckedChange={setAll} />}
 *     expanded={open}
 *     onToggle={() => setOpen(!open)}
 *   >
 *     <PermissionRow control={<Checkbox id="p1" />} htmlFor="p1">
 *       Create calls
 *     </PermissionRow>
 *   </PermissionGroup>
 *
 * The role editor is the one screen with this shape. The expanded state and
 * the checkbox wiring stay with it; this is the box, the lid and the rule.
 */
export const PermissionGroup = React.forwardRef<
  HTMLDivElement,
  PermissionGroupProps
>(function PermissionGroup(
  {
    control,
    title,
    summary,
    badge,
    expanded = false,
    onToggle,
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <Surface
      ref={ref}
      bordered
      radius="lg"
      tone="none"
      className={cn('overflow-hidden', className)}
      {...props}
    >
      <Inline
        gap={0}
        align="center"
        justify="between"
        className={permissionGroupHeaderRecipe()}
        onClick={onToggle}
      >
        <Inline gap={3} align="center">
          {control ? (
            <Inline gap={0} onClick={(event) => event.stopPropagation()}>
              {control}
            </Inline>
          ) : null}
          <Text variant="body-lg-strong" as="span">
            {title}
          </Text>
          {summary ? (
            <Text variant="body" tone="muted" as="span">
              {summary}
            </Text>
          ) : null}
          {badge}
        </Inline>
        <Icon
          icon={expanded ? ChevronUp : ChevronDown}
          size="md"
          tone="muted"
        />
      </Inline>
      {expanded ? (
        <Stack gap={0} className={permissionGroupBodyRecipe()}>
          {children}
        </Stack>
      ) : null}
    </Surface>
  );
});

export interface PermissionRowProps
  extends React.HTMLAttributes<HTMLDivElement>,
    PermissionRowRecipeProps {
  /** The checkbox. */
  control: React.ReactNode;
  /** The control's id, so clicking the words toggles it. */
  htmlFor?: string;
  /** Greyed and not clickable — inherited from the role, or locked. */
  disabled?: boolean;
  /** The permission's name, and any badge that follows it. */
  children: React.ReactNode;
  /**
   * What ticking it lets someone do, under the name. An owner choosing
   * checkboxes saw only names like "View rates", and could not tell that one
   * also priced invoices.
   */
  description?: React.ReactNode;
  /** The description's id, for the control's aria-describedby. */
  descriptionId?: string;
}

/**
 * One permission inside a PermissionGroup: a checkbox, its name and what it
 * lets someone do, and a tint when the row differs from what the role would
 * give.
 */
export const PermissionRow = React.forwardRef<
  HTMLDivElement,
  PermissionRowProps
>(function PermissionRow(
  {
    control,
    htmlFor,
    disabled = false,
    overridden,
    description,
    descriptionId,
    className,
    children,
    ...props
  },
  ref
) {
  const row = (
    <Inline gap={3} align="center">
      {control}
      <Label
        htmlFor={htmlFor}
        className={permissionRowLabelRecipe({ disabled })}
      >
        {children}
      </Label>
    </Inline>
  );
  return (
    <Stack
      ref={ref}
      gap={0}
      className={cn(permissionRowRecipe({ overridden }), className)}
      {...props}
    >
      {row}
      {description ? (
        <p id={descriptionId} className={permissionRowDescriptionRecipe()}>
          {description}
        </p>
      ) : null}
    </Stack>
  );
});

export interface PermissionItemProps {
  id: string;
  label: React.ReactNode;
  /** What ticking it lets someone do; see PermissionRow. */
  description?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  /** Granted by the role itself and not the editor's to take away. */
  locked?: boolean;
  /** Differs from what the role would give this user; tinted and tagged. */
  isOverridden?: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * A PermissionRow with its checkbox and its "Modified" tag already wired:
 * one permission, as the role editor lists it.
 */
export function PermissionItem({
  id,
  label,
  description,
  checked,
  disabled = false,
  locked = false,
  isOverridden = false,
  onChange,
}: PermissionItemProps) {
  const descriptionId = description ? `${id}-description` : undefined;
  return (
    <PermissionRow
      htmlFor={id}
      disabled={disabled || locked}
      overridden={isOverridden}
      description={description}
      descriptionId={descriptionId}
      control={
        <Checkbox
          id={id}
          aria-describedby={descriptionId}
          checked={checked}
          disabled={disabled || locked}
          onCheckedChange={(next) => onChange(next === true)}
        />
      }
    >
      {label}
      {isOverridden && <Badge tone="warning">Modified</Badge>}
    </PermissionRow>
  );
}

export interface Permission {
  id: string;
  label: string;
  /** What ticking it lets someone do, from the permission catalogue. */
  description?: string;
  checked: boolean;
  disabled?: boolean;
  locked?: boolean;
  isOverridden?: boolean;
}

export interface PermissionCategoryProps {
  id: string;
  title: React.ReactNode;
  permissions: Permission[];
  onPermissionChange: (
    categoryId: string,
    permissionId: string,
    checked: boolean
  ) => void;
  /** The select-all checkbox in the header. */
  onCategoryChange: (categoryId: string, checked: boolean) => void;
  /** Tag rows that differ from the role, and count them in the header. */
  showOverrideIndicator?: boolean;
}

/**
 * A PermissionGroup filled from a list: the tally, the select-all checkbox
 * (indeterminate while only some rows are on), the modified count and one
 * PermissionItem per permission. Starts unfolded; the fold is its own.
 *
 *   <PermissionCategory
 *     id="dispatch"
 *     title="Dispatch"
 *     permissions={[{ id: 'create', label: 'Create calls', checked: true }]}
 *     onPermissionChange={(category, permission, checked) => …}
 *     onCategoryChange={(category, checked) => …}
 *   />
 */
export function PermissionCategory({
  id,
  title,
  permissions,
  onPermissionChange,
  onCategoryChange,
  showOverrideIndicator = false,
}: PermissionCategoryProps) {
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Locked rows count towards the tally: the header says how much of the
  // category is on, not how much of it the editor may change.
  const totalCount = permissions.length;
  const checkedCount = permissions.filter((p) => p.checked).length;
  const overriddenCount = permissions.filter((p) => p.isOverridden).length;
  const isAllChecked = checkedCount === totalCount && totalCount > 0;
  const isPartiallyChecked = checkedCount > 0 && checkedCount < totalCount;

  return (
    <PermissionGroup
      title={title}
      summary={`${checkedCount}/${totalCount} permissions`}
      badge={
        showOverrideIndicator && overriddenCount > 0 ? (
          <Badge tone="warning">{overriddenCount} modified</Badge>
        ) : null
      }
      control={
        <Checkbox
          id={`category-${id}`}
          aria-label={
            typeof title === 'string' ? `All ${title} permissions` : undefined
          }
          checked={isPartiallyChecked ? 'indeterminate' : isAllChecked}
          onCheckedChange={(next) => onCategoryChange(id, next === true)}
        />
      }
      expanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      {permissions.map((permission) => (
        <PermissionItem
          key={permission.id}
          id={permission.id}
          label={permission.label}
          description={permission.description}
          checked={permission.checked}
          disabled={permission.disabled}
          locked={permission.locked}
          isOverridden={showOverrideIndicator && permission.isOverridden}
          onChange={(checked) => onPermissionChange(id, permission.id, checked)}
        />
      ))}
    </PermissionGroup>
  );
}
